import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { AuthenticationManagerOperation } from '../../../../shared/operations/auth/authentication-manager.operation';
import { AuthApiService } from '../../../services/auth.api.service';
import { PasskeyApiService } from '../../../services/passkey.api.service';
import { UserState } from '../../../states/user-state';
import { ActivatedRoute, Router } from '@angular/router';
import { BrowserStorageService, StorageKeys } from '../../../services/browser-storage.service';
import { UserModel } from '../../../../shared/models/user.model';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LoanApplicationService } from '../../../services/loan/loan-application.api.service';
import CBValidators from '../../../../shared/validators';
import { environment } from '../../../../../environments/environment';
import { isEmailNotVerifiedError, userFacingErrorMessage } from '../../../utils/user-facing-error';

type LoginStep = 'email' | 'password' | 'passkey' | 'otp';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, AfterViewInit {
    loanId: string | undefined = undefined;
    email: string = '';
    password: string = '';
    hide = true;
    keepLoggedIn = false;
    quote: string = '';
    authenticationManager: AuthenticationManagerOperation;
    @ViewChild('emailInput') emailInputElement!: ElementRef;
    fg: FormGroup;
    errMsg = '';
    showResendVerification = false;
    resendMsg = '';
    inviteToken: string | null = null;
    loginStep: LoginStep = 'email';
    passkeyOptions: unknown = null;
    otpEmail = '';
    otpLoading = false;

    readonly onlineBankingUrl = environment.onlineBankingCommercialLoanUrl;

    constructor(
        private authService: AuthApiService,
        private passkeyApi: PasskeyApiService,
        private userState: UserState,
    private router: Router,
    private route: ActivatedRoute,
        private browserStorageService: BrowserStorageService, // Inject LocalStorageService
        private fb: FormBuilder,
        private loanService: LoanApplicationService,
    ) {
        this.authenticationManager = new AuthenticationManagerOperation(this.authService, userState, router, browserStorageService);
        this.fg = this.fb.group({
            username: this.fb.nonNullable.control('', [Validators.required, CBValidators.email]),
            password: this.fb.nonNullable.control('', Validators.required),
            keep: this.fb.nonNullable.control(''),
            otp: this.fb.nonNullable.control('', [Validators.pattern(/^\d{6}$/)]),
        });
    }

    ngAfterViewInit() {
        if (this.emailInputElement) {
            this.emailInputElement.nativeElement.focus();
        }
    }

    ngOnInit(): void {
        // If a token is present in the URL, prefer it and skip auto-login from storage
        const urlToken = this.route.snapshot.queryParamMap.get('token');
        if (urlToken) {
            this.handleSsoTokenFromUrl();
            return;
        }

        if (this.route.snapshot.queryParamMap.get('saml_error') === '1') {
            this.errMsg =
                'We could not complete sign-in from online banking. Please try again, sign in with your email and password below, or contact Central Bank customer service.';
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { saml_error: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }

        const email = this.route.snapshot.queryParamMap.get('email');
        if (email) {
            this.usernameControl.setValue(email);
        }
        this.inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');

        this.autoLogin();
    }

    continueAfterEmail(): void {
        this.errMsg = '';
        this.resendMsg = '';
        this.showResendVerification = false;
        this.usernameControl.markAsTouched();
        if (this.usernameControl.invalid) {
            this.errMsg = 'Please enter a valid email address.';
            return;
        }
        const email = this.usernameControl.value;
        this.passkeyApi.getAuthenticateOptions(email).subscribe({
            next: (res) => {
                if (res.usePasskey && res.options) {
                    this.passkeyOptions = res.options;
                    this.loginStep = 'passkey';
                } else {
                    this.loginStep = 'password';
                }
            },
            error: () => {
                this.loginStep = 'password';
            }
        });
    }

    backToEmail(): void {
        this.loginStep = 'email';
        this.passkeyOptions = null;
        this.errMsg = '';
    }

    signInWithPasskey(): void {
        this.errMsg = '';
        if (!this.passkeyOptions) {
            this.errMsg = 'Unable to sign in with passkey. Please use your password.';
            this.loginStep = 'password';
            return;
        }
        this.passkeyApi.startPasskeyAuthentication(this.passkeyOptions).then((assertionResponse) => {
            this.passkeyApi.verifyAuthentication(assertionResponse).subscribe({
                next: (user: UserModel) => {
                    this.authenticationManager.setUserFromPasskey(user, !!this.keepControl.value);
                    const inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
                    if (inviteToken) {
                        this.router.navigate(['/invite/nonjoint'], { queryParams: { token: inviteToken } });
                        return;
                    }
                    this.router.navigate(['/loan/application-list']);
                },
                error: (err) => {
                    console.error('Passkey sign-in failed', err);
                    this.errMsg = userFacingErrorMessage(err, 'passkeySignIn');
                }
            });
        }).catch((err) => {
            if (err?.name === 'NotAllowedError') {
                this.errMsg = 'Passkey sign-in was cancelled.';
            } else {
                this.errMsg = 'Passkey sign-in failed. Try your password.';
            }
        });
    }

    login(): void {
        this.resendMsg = '';
        this.showResendVerification = false;
        if (this.usernameControl.invalid || this.passwordControl.invalid) {
            this.usernameControl.markAsTouched();
            this.passwordControl.markAsTouched();
            return;
        }
        this.authenticationManager
            .login(this.usernameControl.value, this.passwordControl.value, !!this.keepControl.value)
            .pipe(
                tap((res: UserModel | { requiresOtp?: true; email: string }) => {
                    const needsOtp = res && 'requiresOtp' in res && res.requiresOtp;
                    if (needsOtp && res && 'email' in res) {
                        this.otpEmail = res.email;
                        this.loginStep = 'otp';
                        this.errMsg = '';
                        this.fg.get('otp')?.setValue('');
                        return;
                    }
                    const user = res as UserModel;
                    const inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
                    if (inviteToken) {
                        this.router.navigate(['/invite/nonjoint'], { queryParams: { token: inviteToken } });
                        return;
                    }
                    this.router.navigate(['/loan/application-list']);
                }),
                catchError((error: any) => {
                    if (isEmailNotVerifiedError(error)) {
                        this.errMsg = userFacingErrorMessage(error, 'login');
                        this.showResendVerification = true;
                    } else {
                        this.errMsg = userFacingErrorMessage(error, 'login');
                    }
                    console.error('Login failed', error);
                    return of(null);
                })
            )
            .subscribe();
    }

    submitOtp(): void {
        if (this.otpLoading) return;
        this.errMsg = '';
        this.otpControl.markAsTouched();
        const otp = String(this.otpControl.value || '').trim();
        if (!/^\d{6}$/.test(otp) || !this.otpEmail) {
            this.errMsg = 'Please enter the 6-digit code from your email.';
            return;
        }
        this.otpLoading = true;
        this.authService.verifyAdminOtp(this.otpEmail, otp).subscribe({
            next: (user: UserModel) => {
                this.otpLoading = false;
                this.authenticationManager.setUserFromPasskey(user, !!this.keepControl.value);
                const inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
                if (inviteToken) {
                    this.router.navigate(['/invite/nonjoint'], { queryParams: { token: inviteToken } });
                    return;
                }
                this.router.navigate(['/loan/application-list']);
            },
            error: (err) => {
                this.otpLoading = false;
                console.error('Admin OTP verification failed', err);
                this.errMsg = 'Invalid or expired code. Try signing in again.';
            }
        });
    }

    backFromOtp(): void {
        this.loginStep = 'password';
        this.errMsg = '';
        this.otpControl.setValue('');
    }

    resendVerification = (_e: Event) => {
        this.resendMsg = '';
        const email = String(this.usernameControl.value || '').trim();
        if (!email) {
            this.usernameControl.markAsTouched();
            this.resendMsg = 'Please enter your email address first.';
            return;
        }
        if (this.usernameControl.invalid) {
            this.usernameControl.markAsTouched();
            this.resendMsg = 'Please enter a valid email address.';
            return;
        }

        this.authService.resendVerification(email).subscribe({
            next: (res) => {
                this.resendMsg = res?.message || 'Verification link sent. Please check your inbox.';
            },
            error: (err) => {
                console.error('Resend verification failed', err);
                this.resendMsg = 'Unable to resend verification right now.';
            }
        });
    };

    /**
     * If the login page is loaded with SSO query params (?token=...&email=...),
     * automatically perform token-based login and navigate into the app.
     */
    private handleSsoTokenFromUrl(): void {
        const token = this.route.snapshot.queryParamMap.get('token') ?? undefined;
        if (!token) return;

        const ssoMerged = this.route.snapshot.queryParamMap.get('sso_merged') === '1';

        let email = this.route.snapshot.queryParamMap.get('email') ?? undefined;
        if (!email) {
            try {
                const parts = String(token).split('.');
                if (parts.length === 3 && typeof window !== 'undefined' && typeof atob === 'function') {
                    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                    const json = decodeURIComponent(
                        atob(base64)
                            .split('')
                            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                            .join(''),
                    );
                    const payload = JSON.parse(json);
                    email = payload?.email ?? payload?.username ?? undefined;
                }
            } catch (e) {
                console.warn('Failed to decode JWT from URL token', e);
            }
        }

        if (!email) {
            this.errMsg =
                'We could not complete sign-in from online banking. Please sign in with your email and password below.';
            const inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
            void this.router.navigate(['/login'], {
                replaceUrl: true,
                queryParams: inviteToken ? { inviteToken } : {},
            });
            return;
        }

        this.authenticationManager
            .relogin(email, token, true)
            .pipe(
                tap((_user: UserModel) => {
                    if (ssoMerged && typeof sessionStorage !== 'undefined') {
                        sessionStorage.setItem('cb_sso_merged_banner', '1');
                    }
                    const inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
                    if (inviteToken) {
                        this.router.navigate(['/invite/nonjoint'], {
                            queryParams: { token: inviteToken },
                            replaceUrl: true,
                        });
                        return;
                    }
                    this.router.navigate(['/loan/application-list'], { replaceUrl: true });
                }),
                catchError((error: any) => {
                    console.error('SSO token login failed', error);
                    this.errMsg =
                        'We could not complete sign-in. Try again from online banking or use your email and password below.';
                    const inviteToken = this.route.snapshot.queryParamMap.get('inviteToken');
                    void this.router.navigate(['/login'], {
                        replaceUrl: true,
                        queryParams: inviteToken ? { inviteToken } : {},
                    });
                    return of(null);
                }),
            )
            .subscribe();
    }

    private autoLogin(): void {
        const token = this.userState.getUser()?.token ?? undefined;
        const username = this.userState.getUser()?.email ?? undefined;

        if (username && token) {
            this.authenticationManager
                .relogin(username, token, !!this.browserStorageService.getLocalItem(StorageKeys.Authentication))
                .subscribe(
                    (user: UserModel) => {
                        this.router.navigate(['/loan/application-list']);
                    },
                    (error: any) => {
                        console.error('Auto login failed', error);
                    }
                );
        }
    }

    clickEvent(event: MouseEvent): void {
        this.hide = !this.hide;
        event.stopPropagation();
    }

    get usernameControl() {
        return this.fg.get('username') as FormControl;
    }
    get passwordControl() {
        return this.fg.get('password') as FormControl;
    }
    get keepControl() {
        return this.fg.get('keep') as FormControl;
    }

    get otpControl() {
        return this.fg.get('otp') as FormControl;
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(_event: KeyboardEvent): void {
        if (this.loginStep === 'email') {
            this.continueAfterEmail();
        } else if (this.loginStep === 'password') {
            this.login();
        } else if (this.loginStep === 'otp') {
            this.submitOtp();
        }
    }
}
